/**
 * Frozen wire contract and lifecycle vocabulary for the live-updates WebSocket.
 *
 * `action`-keyed frames are client -> server (API Gateway route selection).
 * `type`-keyed frames are server -> client. The reducer acts only on the
 * `auth_ok` / `auth_error` control frames; every other `type` is an opaque
 * data frame that is forwarded verbatim.
 */

// ---------------------------------------------------------------------------
// Wire frames
// ---------------------------------------------------------------------------

/** client -> server */
export type AuthFrame = { action: 'auth'; token: string };
export type HeartbeatFrame = { action: 'heartbeat' };
export type ClientFrame = AuthFrame | HeartbeatFrame;

/** server -> client control frames the reducer acts on */
export type AuthOkFrame = { type: 'auth_ok' };
export type AuthErrorFrame = { type: 'auth_error'; reason?: string };

/**
 * Refetch signal: identifiers only, no message content or conversation ID.
 */
export type MessageCreatedFrame = {
  type: 'message.created';
  leasing_team_id: number;
  lead_uuid: string;
  ts: number;
};

/**
 * Any server frame whose `type` is not an auth control frame is an opaque data
 * frame. The reducer never inspects it beyond `type`; it forwards it via
 * `EMIT_FRAME`, so new signal types need no reducer change.
 */
export type DataFrame = { type: string; [key: string]: unknown };

/**
 * API Gateway idle prevention under its 600-second cap.
 */
export const HEARTBEAT_INTERVAL_MS = 270_000;
/**
 * Separate 15-second deadlines bound socket opening and application auth so a
 * stalled phase returns to polling/backoff instead of hanging indefinitely.
 */
export const PHASE_TIMEOUT_MS = 15_000;
/** ~10 min under the un-increasable 2h hard connection cap. */
export const PROACTIVE_RECONNECT_MS = 6_600_000;
/** Exponential reconnect backoff base. */
export const BACKOFF_BASE_MS = 1_000;
/** Exponential reconnect backoff cap. */
export const BACKOFF_CAP_MS = 30_000;

/**
 * State machine vocabulary
 */
export type WsPhase =
  'idle' | 'connecting' | 'authenticating' | 'live' | 'backoff' | 'closed';

export type WsState = {
  phase: WsPhase;
  /** Reconnect attempt counter; drives the backoff delay. Reset to 0 on auth_ok. */
  attempt: number;
  /** Latest ws_token to present on the auth frame; null until one is supplied. */
  token: string | null;
  /** Feature enabled (flag-on + authed), tracked via ENABLE / DISABLE. */
  enabled: boolean;
  /** Why a token refresh is in flight or waiting to retry. */
  refresh: 'idle' | 'auth' | 'proactive' | 'proactive-wait';
  /** Proactive refresh failure count; independent from connection failures. */
  refreshAttempt: number;
};

export type WsEvent =
  | { type: 'ENABLE' }
  | { type: 'DISABLE' }
  | { type: 'TOKEN_READY'; token: string }
  | { type: 'REFRESH_SUCCEEDED'; token: string }
  | { type: 'REFRESH_FAILED' }
  | { type: 'SOCKET_OPEN' }
  // The shell could not construct a socket (missing/malformed server URL);
  // no open/close/error will ever arrive, so the shell feeds this back in.
  | { type: 'OPEN_FAILED' }
  | { type: 'PHASE_TIMEOUT' }
  | { type: 'AUTH_OK' }
  | { type: 'AUTH_ERROR'; reason?: string }
  | { type: 'FRAME'; frame: DataFrame }
  | { type: 'CLOSE' }
  | { type: 'ERROR' }
  | { type: 'HEARTBEAT_TICK' }
  | { type: 'RECONNECT_TICK' }
  | { type: 'REFRESH_RETRY_TICK' }
  | { type: 'PROACTIVE_TICK' };

/** The independent timers the shell runs on the reducer's behalf. */
export type TimerKey = 'heartbeat' | 'phase' | 'proactive' | 'reconnect' | 'refreshRetry';

export type WsCommand =
  | { type: 'OPEN_SOCKET' }
  | { type: 'CLOSE_SOCKET' }
  | { type: 'SEND'; frame: ClientFrame }
  // Start `timer` after `delayMs`, replacing any pending instance. `jitter` asks
  // the shell to spread the delay uniformly across `[0, delayMs]`; the reducer
  // decides only whether to jitter, never by how much, so its output stays
  // deterministic.
  | { type: 'SCHEDULE'; timer: TimerKey; delayMs: number; jitter: boolean }
  // Cancel one timer, or every timer when `timer` is omitted.
  | { type: 'CLEAR'; timer?: TimerKey }
  | { type: 'REFRESH_TOKEN' }
  | { type: 'EMIT_FRAME'; frame: DataFrame };
